import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Search, ShoppingCart, ArrowUpDown, SlidersHorizontal, X, Zap, Truck, Sparkles, Gauge, Music, Trophy, Fuel, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { useCart, useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryNames = {
  hoodies: "Hoodies",
  shirts: "T-Shirts",
  pants: "Pants",
  jackets: "Jackets",
  phone_cases: "Phone Cases",
  stickers: "Stickers",
  cups: "Mugs & Drinkware",
  posters: "Posters & Wall Art",
  hats: "Hats & Headwear",
  tank_tops: "Tank Tops",
  joggers: "Joggers",
  tote_bags: "Tote Bags",
  laptop_sleeves: "Laptop Sleeves",
  backpacks: "Backpacks",
  socks: "Socks",
  mouse_pads: "Desk Pads",
  blankets: "Blankets",
  flags: "Flags & Banners",
};

const collectionIcons = {
  idle_to_redline: Gauge,
  gasmadethebeat: Music,
  gapped_and_glorious: Trophy,
  neon_tokyo: Sparkles,
  essentials: Fuel,
};

const MerchPage = () => {
  const [merch, setMerch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCollection, setSelectedCollection] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [featuredItems, setFeaturedItems] = useState([]);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const res = await axios.get(`${API}/merch/collections`);
        setCollections(res.data || []);
      } catch (err) {
        console.error("Error fetching collections:", err);
      }
    };
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/merch/categories/list`);
        setCategories(res.data.categories || []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${API}/merch/featured?limit=4`);
        setFeaturedItems(res.data || []);
      } catch (err) {
        console.error("Error fetching featured:", err);
      }
    };
    fetchCollections();
    fetchCategories();
    fetchFeatured();
  }, []);

  useEffect(() => {
    const fetchMerch = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCategory !== "all") params.category = selectedCategory;
        if (selectedCollection !== "all") params.collection = selectedCollection;
        if (searchQuery) params.search = searchQuery;
        if (priceRange[0] > 0) params.price_min = priceRange[0];
        if (priceRange[1] < 500) params.price_max = priceRange[1];
        if (sortBy !== "newest") params.sort_by = sortBy;
        const response = await axios.get(`${API}/merch`, { params });
        setMerch(response.data);
      } catch (err) {
        console.error("Error fetching merch:", err);
      } finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(fetchMerch, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedCategory, selectedCollection, sortBy, priceRange]);

  const handleQuickAdd = async (item) => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return;
    }
    const defaultVariant = item.variants?.[0];
    await addToCart({
      item_type: "merch",
      item_id: item.merch_id,
      size: defaultVariant?.size,
      color: defaultVariant?.color,
      quantity: 1
    });
  };

  const clearFilters = () => {
    setSelectedCategory("all");
    setSelectedCollection("all");
    setSearchQuery("");
    setSortBy("newest");
    setPriceRange([0, 500]);
  };

  const activeFilters = (selectedCategory !== "all" ? 1 : 0) + (selectedCollection !== "all" ? 1 : 0) + (priceRange[0] > 0 || priceRange[1] < 500 ? 1 : 0);
  const showHero = selectedCollection === "all" && !searchQuery;

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6" data-testid="merch-page">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-4">
            Merch Store
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-3">
            Exclusive Merch
          </h1>
          <p className="text-zinc-400 max-w-2xl text-sm sm:text-base">
            Limited edition streetwear and accessories. Fulfilled by Printify — ships worldwide.
          </p>
          <div className="flex gap-3 mt-4">
            <a href="/merch-studio" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-magenta/20 text-neon-magenta border border-neon-magenta/30 text-sm hover:bg-neon-magenta hover:text-black transition" data-testid="merch-studio-link">
              Design Your Own
            </a>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/20 text-sm">
              <Truck className="w-4 h-4" /> Free Shipping $50+
            </div>
          </div>
        </div>

        {/* Collection Cards */}
        {showHero && collections.length > 0 && (
          <div className="mb-10">
            <h2 className="font-heading text-base sm:text-lg uppercase tracking-wide text-zinc-300 mb-4">Shop by Collection</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {collections.map((coll, idx) => {
                const Icon = collectionIcons[coll.collection_id] || Package;
                return (
                  <motion.button
                    key={coll.collection_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => setSelectedCollection(coll.collection_id)}
                    className="group relative overflow-hidden rounded-lg border border-white/10 p-4 text-left transition-all hover:border-white/30 hover:scale-[1.02]"
                    style={{ background: `linear-gradient(135deg, ${coll.color}15, transparent)` }}
                    data-testid={`collection-${coll.collection_id}`}
                  >
                    <Icon className="w-5 h-5 mb-2" style={{ color: coll.color }} />
                    <p className="font-heading text-sm uppercase tracking-wide truncate">{coll.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">{coll.product_count} items</p>
                    <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: coll.color, opacity: 0.5 }} />
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* Featured Items */}
        {showHero && featuredItems.length > 0 && (
          <div className="mb-10">
            <h2 className="font-heading text-base sm:text-lg uppercase tracking-wide text-zinc-300 mb-4">Featured</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredItems.map((item, idx) => (
                <motion.div
                  key={item.merch_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08 }}
                >
                  <Link to={`/merch/${item.merch_id}`} className="block group">
                    <div className="relative aspect-square overflow-hidden bg-zinc-900 border border-white/5 rounded-lg">
                      <img
                        src={item.images[0] || ""}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      {item.is_dropshipped && (
                        <Badge className="absolute top-3 right-3 bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30 text-[10px]">
                          <Truck className="w-3 h-3 mr-1" /> Ships Worldwide
                        </Badge>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-neon-magenta font-mono font-bold">${item.price.toFixed(2)}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Active Collection Header */}
        {selectedCollection !== "all" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center justify-between"
          >
            <div>
              {(() => {
                const coll = collections.find(c => c.collection_id === selectedCollection);
                if (!coll) return null;
                const Icon = collectionIcons[coll.collection_id] || Package;
                return (
                  <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6" style={{ color: coll.color }} />
                    <div>
                      <h2 className="font-heading text-xl sm:text-2xl uppercase" style={{ color: coll.color }}>{coll.name}</h2>
                      <p className="text-xs text-zinc-500">{coll.description}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedCollection("all")}
              className="text-zinc-400"
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </motion.div>
        )}

        {/* Search & Sort Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Search merch..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/10 h-11"
              data-testid="merch-search"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-44 bg-black/50 border-white/10 h-11" data-testid="sort-filter">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className={`border-white/10 h-11 ${showFilters ? 'bg-neon-magenta/20 border-neon-magenta/50' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-button"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {activeFilters > 0 && (
              <span className="ml-2 w-5 h-5 bg-neon-magenta text-black text-xs font-bold rounded-full flex items-center justify-center">
                {activeFilters}
              </span>
            )}
          </Button>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="glass p-5 mb-6 overflow-hidden"
              data-testid="filters-panel"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-sm uppercase tracking-wide text-zinc-400">Filters</h3>
                {activeFilters > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 text-xs">
                    <X className="w-3 h-3 mr-1" /> Clear All
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Collection</label>
                  <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                    <SelectTrigger className="bg-black/50 border-white/10" data-testid="collection-filter">
                      <SelectValue placeholder="All Collections" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="all">All Collections</SelectItem>
                      {collections.map((coll) => (
                        <SelectItem key={coll.collection_id} value={coll.collection_id}>{coll.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Category</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="bg-black/50 border-white/10" data-testid="category-filter">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{categoryNames[cat] || cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">
                    Price: <span className="text-neon-magenta">${priceRange[0]} - ${priceRange[1]}</span>
                  </label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={0}
                    max={500}
                    step={5}
                    className="mt-3"
                    data-testid="price-slider"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={selectedCategory === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory("all")}
            className={selectedCategory === "all" ? "bg-neon-magenta text-black" : "border-white/20"}
          >
            All
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className={selectedCategory === cat ? "bg-neon-magenta text-black" : "border-white/20"}
            >
              {categoryNames[cat] || cat}
            </Button>
          ))}
        </div>

        {/* Results Count */}
        {!loading && (
          <p className="text-xs text-zinc-500 mb-4 uppercase tracking-wide" data-testid="results-count">
            {merch.length} item{merch.length !== 1 ? 's' : ''} found
          </p>
        )}

        {/* Merch Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-zinc-900/50 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : merch.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">No merch found matching your criteria</p>
            <Button variant="outline" onClick={clearFilters} className="border-white/20">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {merch.map((item, index) => (
              <motion.div
                key={item.merch_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.5) }}
                data-testid={`merch-card-${item.merch_id}`}
              >
                <div className="group relative aspect-[3/4] overflow-hidden bg-zinc-900 border border-white/5 rounded-lg card-hover">
                  <Link to={`/merch/${item.merch_id}`}>
                    <img
                      src={item.images[0] || ""}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1">
                      <Badge className="bg-black/60 text-white border-0 text-[10px] uppercase">
                        {categoryNames[item.category] || item.category}
                      </Badge>
                    </div>

                    <div className="absolute top-3 right-3 flex flex-col gap-1">
                      {item.is_dropshipped && (
                        <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30 text-[10px]">
                          <Truck className="w-3 h-3 mr-0.5" /> Ships
                        </Badge>
                      )}
                      {item.on_sale && (
                        <Badge className="bg-red-500 text-white border-0 text-[10px] font-bold" data-testid={`sale-badge-${item.merch_id}`}>
                          <Zap className="w-3 h-3 mr-0.5" />{item.sale_discount}% OFF
                        </Badge>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                      <p className="font-medium text-sm truncate mb-1">{item.name}</p>
                      {item.on_sale ? (
                        <div className="flex items-center gap-2">
                          <p className="text-red-400 font-mono font-bold">${item.price.toFixed(2)}</p>
                          <p className="text-zinc-500 font-mono text-xs line-through">${item.original_price?.toFixed(2)}</p>
                        </div>
                      ) : (
                        <p className="text-neon-magenta font-mono font-bold">
                          ${item.price.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </Link>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/60">
                    <div className="flex flex-col gap-2">
                      <Link to={`/merch/${item.merch_id}`}>
                        <Button className="bg-white text-black font-bold uppercase text-xs sm:text-sm" data-testid={`view-merch-${item.merch_id}`}>
                          View Details
                        </Button>
                      </Link>
                      <Button
                        onClick={(e) => { e.preventDefault(); handleQuickAdd(item); }}
                        className="bg-neon-magenta text-black font-bold uppercase text-xs sm:text-sm"
                        data-testid={`quick-add-${item.merch_id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Quick Add
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchPage;
